import React from "react";
import { Link } from "react-router-dom";
import { FaArrowRight } from "react-icons/fa";
import TechStackCitations from "./TechStackCitations";
import useSEO from "./useSEO";

const WhyPage = () => {
  useSEO({
    title: "Why Choose Core Spectators | Elite Software, Shopify & App Engineering",
    description: "Discover why ambitious founders and scale-ups partner with Core Spectators: rapid zero-to-one SaaS engineering, bespoke high-converting Shopify stores, native Flutter apps, and intelligent AI workflow automations.",
    keywords: "why choose Core Spectators, software engineering agency, custom shopify store developers, flutter mobile app development, saas mvp architecture, rapid time to market, full stack developers, reliable tech partner",
    canonical: "https://corespectators.com/Why-us",
    schema: {
      "@context": "https://schema.org",
      "@type": "WebPage",
      "name": "Why Choose Core Spectators",
      "url": "https://corespectators.com/Why-us",
      "description": "Discover our software engineering principles, Shopify e-commerce mastery, mobile app architecture, and agile execution speed."
    }
  });

  return (
    <div className="bg-white py-10">
      {/* 1. Expertise & Dedicated Team */}
      <div className="sm:flex items-center m-auto max-w-screen-xl px-4 py-8">
        <div className="sm:w-1/2 ml-0 sm:ml-8 p-4 sm:p-5">
          <div className="text">
            <span className="text-red-600 font-semibold uppercase tracking-wider text-sm border-b-2 border-red-600 pb-1 inline-block mb-3">
              Why Us
            </span>
            <h1 className="my-2 font-extrabold text-3xl sm:text-4xl text-gray-900 leading-tight">
              Why Choose <span className="text-red-600">Core Spectators?</span>
            </h1>
            <div className="sm:hidden block p-4 py-5 flex justify-center">
              <img src="/images/wp1.svg" alt="Core Spectators AI Team" className="w-full max-h-[320px] object-contain" />
            </div>
            <p className="mb-4 text-gray-600 text-sm sm:text-base leading-relaxed">
              We bring together deep domain expertise across bespoke Shopify 2.0 storefronts, cross-platform Flutter mobile applications, multi-tenant SaaS platforms, and autonomous AI workflow systems. We architect solutions with an engineering-first, business-driven mindset.
            </p>
            <p className="text-gray-600 text-sm sm:text-base leading-relaxed">
              Our multidisciplinary squad integrates directly into your roadmap. By combining clean modular software design with rigorous automated testing, we deliver production-ready systems that eliminate bottlenecks and scale smoothly.
            </p>
          </div>
        </div>
        <div className="sm:w-1/2 hidden sm:flex justify-center p-6 sm:p-10">
          <img src="/images/wp1.svg" alt="Core Spectators AI Team" className="w-full max-h-[380px] object-contain drop-shadow" />
        </div>
      </div>

      {/* 2. Client Success */}
      <div className="sm:flex items-center m-auto max-w-screen-xl px-4 py-8 border-t border-gray-100">
        <div className="sm:w-1/2 hidden sm:flex justify-center p-6 sm:p-10">
          <img src="/images/wp2.svg" alt="Client Success Focus" className="w-full max-h-[380px] object-contain drop-shadow" />
        </div>
        <div className="sm:w-1/2 ml-0 sm:ml-8 p-4 sm:p-5">
          <div className="text">
            <span className="text-red-600 font-semibold uppercase tracking-wider text-sm border-b-2 border-red-600 pb-1 inline-block mb-3">
              Satisfaction & ROI
            </span>
            <h2 className="my-2 font-extrabold text-3xl sm:text-4xl text-gray-900 leading-tight">
              Obsessed With <span className="text-red-600">Technical Rigor & Performance</span>
            </h2>
            <div className="sm:hidden block p-4 py-5 flex justify-center">
              <img src="/images/wp2.svg" alt="Client Success Focus" className="w-full max-h-[320px] object-contain" />
            </div>
            <p className="mb-4 text-gray-600 text-sm sm:text-base leading-relaxed">
              We measure our performance by the tangible stability, speed, and conversion power delivered to your business. Whether optimizing Shopify checkout conversion or architecting a multi-tenant SaaS database, every deliverable is built to enterprise benchmarks.
            </p>
            <p className="text-gray-600 text-sm sm:text-base leading-relaxed">
              This engineering-centric approach fosters long-term strategic partnerships built on trust, transparency, and continuous technological enhancement.
            </p>
          </div>
        </div>  
      </div>

      {/* 3. Integrity & Security */}
      <div className="sm:flex items-center m-auto max-w-screen-xl px-4 py-8 border-t border-gray-100">
        <div className="sm:w-1/2 ml-0 sm:ml-8 p-4 sm:p-5">
          <div className="text">
            <span className="text-red-600 font-semibold uppercase tracking-wider text-sm border-b-2 border-red-600 pb-1 inline-block mb-3">
              Security & Integrity
            </span>
            <h2 className="my-2 font-extrabold text-3xl sm:text-4xl text-gray-900 leading-tight">
              Enterprise Data Security & <span className="text-red-600">Integrity</span>
            </h2>
            <div className="sm:hidden block p-4 py-5 flex justify-center">
              <img src="/images/wp3.svg" alt="Enterprise Security" className="w-full max-h-[320px] object-contain" />
            </div>
            <p className="mb-4 text-gray-600 text-sm sm:text-base leading-relaxed">
              Modern software demands strict data privacy, isolated database tenants, and zero-leakage security architectures. We adhere to defensive security standards, ensuring your proprietary source code and data remain strictly protected.
            </p>
            <p className="text-gray-600 text-sm sm:text-base leading-relaxed">
              We prioritize honest, transparent architectural recommendations—advising on the exact frameworks and tools that yield maximum performance without technical debt.
            </p>
          </div>
        </div>
        <div className="sm:w-1/2 hidden sm:flex justify-center p-6 sm:p-10">
          <img src="/images/wp3.svg" alt="Enterprise Security" className="w-full max-h-[380px] object-contain drop-shadow" />
        </div>
      </div>

      {/* 4. Flexibility */}
      <div className="sm:flex items-center m-auto max-w-screen-xl px-4 py-8 border-t border-gray-100">
        <div className="sm:w-1/2 hidden sm:flex justify-center p-6 sm:p-10">
          <img src="/images/wp4.svg" alt="Modular Flexibility" className="w-full max-h-[380px] object-contain drop-shadow" />
        </div>
        <div className="sm:w-1/2 ml-0 sm:ml-8 p-4 sm:p-5">
          <div className="text">
            <span className="text-red-600 font-semibold uppercase tracking-wider text-sm border-b-2 border-red-600 pb-1 inline-block mb-3">
              Modularity
            </span>
            <h2 className="my-2 font-extrabold text-3xl sm:text-4xl text-gray-900 leading-tight">
              Modular & <span className="text-red-600">Customized Solutions</span>
            </h2>
            <div className="sm:hidden block p-4 py-5 flex justify-center">
              <img src="/images/wp4.svg" alt="Modular Flexibility" className="w-full max-h-[320px] object-contain" />
            </div>
            <p className="mb-4 text-gray-600 text-sm sm:text-base leading-relaxed">
              No two organizations are identical. We provide flexible engagement models—from single Shopify feature engineering to end-to-end full-stack SaaS builds.
            </p> 
            <p className="text-gray-600 text-sm sm:text-base leading-relaxed">
              You choose the services matching your immediate roadmap, giving you maximal agility and high capital efficiency.
            </p> 
          </div>
        </div>
      </div>

      {/* 5. Timely Delivery & CTA */}
      <div className="sm:flex items-center m-auto max-w-screen-xl px-4 py-8 border-t border-gray-100">
        <div className="sm:w-1/2 ml-0 sm:ml-8 p-4 sm:p-5">
          <div className="text">
            <span className="text-red-600 font-semibold uppercase tracking-wider text-sm border-b-2 border-red-600 pb-1 inline-block mb-3">
              Execution
            </span>
            <h2 className="my-2 font-extrabold text-3xl sm:text-4xl text-gray-900 leading-tight">
              Rapid Iteration & <span className="text-red-600">On-Time Delivery</span>
            </h2>
            <div className="sm:hidden block p-4 py-5 flex justify-center">
              <img src="/images/wp6.svg" alt="Timely Delivery" className="w-full max-h-[320px] object-contain" />
            </div>
            <p className="mb-4 text-gray-600 text-sm sm:text-base leading-relaxed">
              In modern digital commerce and SaaS, time-to-market is your competitive edge. We sprint through prototyping, code reviews, and automated CI/CD deployments to deliver working software on schedule.
            </p>
            <div className="mt-6 flex flex-wrap gap-3">
              <Link
                to="/Form"
                className="bg-red-600 hover:bg-red-700 text-white font-bold py-3 px-8 rounded-xl inline-flex items-center gap-2 shadow-lg transition text-sm sm:text-base"
              >
                Submit Project Requirements <FaArrowRight className="text-xs" />
              </Link>
            </div>
          </div>
        </div>
        <div className="sm:w-1/2 hidden sm:flex justify-center p-6 sm:p-10">
          <img src="/images/wp6.svg" alt="Timely Delivery" className="w-full max-h-[380px] object-contain drop-shadow" />
        </div>
      </div>

      {/* Verified Architectural Standards & Authoritative Tech Stack Citations */}
      <TechStackCitations />
    </div>
  );
};

export default WhyPage;